import { supabase } from '@/lib/supabase';

// Función para generar número de pedido secuencial (fallback)
export const generateOrderNumber = async () => {
  try {
    // Obtener el último número de pedido ordenado por number
    const { data: existingOrders, error: fetchError } = await supabase
      .from('orders')
      .select('number')
      .not('number', 'is', null)
      .order('number', { ascending: false })
      .limit(1);

    if (fetchError) throw fetchError;

    // Obtener el último número
    let lastNumber = 0;
    if (existingOrders && existingOrders.length > 0) {
      const lastOrder = existingOrders[0];
      const parsedNumber = parseInt(lastOrder.number);
      if (!isNaN(parsedNumber)) {
        lastNumber = parsedNumber;
      }
    }

    // El siguiente número será el último + 1
    const nextNumber = lastNumber + 1;
    return nextNumber.toString().padStart(6, '0');
  } catch (error) {
    console.error('Error generating order number:', error);
    throw error;
  }
};

// Función para generar número de pedido con bloqueo optimista (más seguro para concurrencia)
export const generateOrderNumberOptimistic = async () => {
  try {
    // Usar una transacción para evitar condiciones de carrera
    const { data, error } = await supabase.rpc('generate_order_number_optimistic');
    
    if (error) {
      return await generateOrderNumber();
    }
    
    return data;
  } catch (error) {
    return await generateOrderNumber();
  }
};

export const saveOrder = async (order) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    const timestamp = new Date().toISOString();

    // Insertar o actualizar pedido
    let orderData;
    let orderError;

    if (order.id) {
      const { data, error } = await supabase
        .from('orders')
        .update({
          number: order.number,
          date: order.date,
          customer_id: order.customerId,
          total: Math.round(order.items.reduce((sum, item) => sum + (item.quantity * item.price), 0) * 100) / 100,
          status: order.status,
          updated_at: timestamp,
          user_id: session.user.id,
          discount_percentage: order.discount_percentage || 0,
          payment_transfer: order.payment_transfer || false
        })
        .eq('id', order.id)
        .select()
        .single();
      orderData = data;
      orderError = error;

      // Eliminar los items anteriores
      await supabase.from('order_items').delete().eq('order_id', order.id);
    } else {
      const { data, error } = await supabase
        .from('orders')
        .insert({
          number: order.number,
          date: order.date,
          customer_id: order.customerId,
          total: Math.round(order.items.reduce((sum, item) => sum + (item.quantity * item.price), 0) * 100) / 100,
          status: order.status,
          updated_at: timestamp,
          user_id: session.user.id,
          discount_percentage: order.discount_percentage || 0,
          payment_transfer: order.payment_transfer || false
        })
        .select()
        .single();
      orderData = data;
      orderError = error;
    }

    if (orderError) throw orderError;

    // Insertar items del pedido
    const orderItems = order.items.map(item => ({
      order_id: orderData.id,
      product_id: item.product_id,
      size: item.size,
      color: item.color,
      quantity: item.quantity,
      price: item.price,
      created_at: timestamp
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) throw itemsError;

    return orderData;
  } catch (error) {
    console.error('Error saving order:', error);
    throw error;
  }
};

export const processOrder = async (order) => {
  try {
    // Verificar stock disponible
    for (const item of order.items) {
      const { data: stockData } = await supabase
        .from('inventory')
        .select('quantity')
        .eq('product_id', item.product_id)
        .eq('size', item.size)
        .eq('color', item.color);

      const currentStock = stockData?.reduce((sum, record) => sum + (record.quantity || 0), 0) || 0;

      if (currentStock < item.pending_quantity) {
        throw new Error(`Stock insuficiente para ${item.product_code} - Talla: ${item.size}, Color: ${item.color}`);
      }
    }

    // Generar número del documento de salida usando la misma función que DocumentForm
    const { generateDocumentNumber } = await import('./documents');
    const newNumber = await generateDocumentNumber('salida');

    // Crear documento de salida
    const { data: document, error: documentError } = await supabase
      .from('documents')
      .insert({
        type: 'salida',
        number: newNumber,
        date: new Date().toISOString().split('T')[0],
        customer_id: order.customer_id,
        total: order.total,
        status: 'completed',
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (documentError) throw documentError;

    // Crear items del documento
    const documentItems = order.items.map(item => ({
      document_id: document.id,
      product_id: item.product_id,
      size: item.size,
      color: item.color,
      quantity: item.pending_quantity,
      price: item.price,
      created_at: new Date().toISOString()
    }));

    const { error: itemsError } = await supabase
      .from('document_items')
      .insert(documentItems);

    if (itemsError) throw itemsError;

    // Actualizar inventario usando la función correcta
    const inventoryUpdates = order.items.map(item => ({
      product_id: item.product_id,
      size: item.size,
      color: item.color,
      pending_orders: item.pending_quantity
    }));

    // Importar la función dinámicamente para evitar dependencias circulares
    const { updateInventoryPendingOrders } = await import('./inventory');
    await updateInventoryPendingOrders(inventoryUpdates, 'subtract');

    // Actualizar estado del pedido
    const { error: orderError } = await supabase
      .from('orders')
      .update({ status: 'served' })
      .eq('id', order.id);

    if (orderError) throw orderError;

    // Actualizar estado de los items del pedido
    const { error: orderItemsError } = await supabase
      .from('order_items')
      .update({ pending_quantity: 0 })
      .eq('order_id', order.id);

    if (orderItemsError) throw orderItemsError;

    return document;
  } catch (error) {
    console.error('Error processing order:', error);
    throw error;
  }
};

export const fetchOrders = async (status = null) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No hay sesión activa');

    // Obtener el rol y permisos del usuario
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role, cod_rep, ver_comisiones_globales')
      .eq('id', session.user.id)
      .single();

    if (userError) throw userError;

    // Construir la consulta base
    let query = supabase
      .from('orders')
      .select(`
        *,
        customer:customers(
          name,
          cod_rep
        ),
        items:order_items(
          *,
          product:products(
            name,
            code
          )
        )
      `)
      .order('date', { ascending: false });

    // Aplicar filtro de estado si se especifica
    if (status) {
      query = query.eq('status', status);
    }

    // Si el usuario es vendedor y no tiene ver_comisiones_globales, filtrar por su cod_rep
    if (userData.role === 'vendedor' && !userData.ver_comisiones_globales) {
      query = query.eq('customer.cod_rep', userData.cod_rep);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const calculatePendingOrders = async (productId, size, color) => {
  try {
    const { data, error } = await supabase
      .from('order_items')
      .select('pending_quantity')
      .eq('product_id', productId)
      .eq('size', size)
      .eq('color', color)
      .eq('status', 'pending');

    if (error) throw error;

    return data?.reduce((sum, item) => sum + (item.pending_quantity || 0), 0) || 0;
  } catch (error) {
    console.error('Error calculating pending orders:', error);
    throw error;
  }
};
